import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';

const RECENT_SEARCHES_KEY = 'jimenez-jewelry-recent-searches';
const MAX_RECENT_SEARCHES = 6;

/**
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput
 * @property {HTMLElement} predictiveSearchResults
 * @property {HTMLElement} resetButton
 * @property {HTMLFormElement} form
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton', 'form'];

  #controller = new AbortController();
  #activeFetch = null;
  #emptyStateMarkup = '';
  #isKeyboardNavigation = false;

  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const { dialog } = this;

    this.#emptyStateMarkup = this.refs.predictiveSearchResults.innerHTML;
    this.#hydrateEmptyState();

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
    }

    this.addEventListener('click', this.#handleClick, { signal });
    this.refs.form.addEventListener('submit', this.#handleFormSubmit, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  get #allResultsItems() {
    return /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('[data-predictive-item]')).filter((item) => item instanceof HTMLElement)
    );
  }

  get #currentIndex() {
    return this.#allResultsItems.findIndex((item) => item.getAttribute('aria-selected') === 'true');
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item, itemIndex) => {
      item.classList.remove('keyboard-focus');
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) item.classList.add('keyboard-focus');
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    });

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems[this.#currentIndex];
  }

  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    this.refs.searchInput.focus();
    this.#hydrateEmptyState();
  };

  #handleFormSubmit = () => {
    this.#storeRecentSearch(this.refs.searchInput.value);
  };

  #handleClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const clearRecentButton = target.closest('[data-clear-recent-searches]');
    if (clearRecentButton) {
      event.preventDefault();
      this.#clearRecentSearches();
      this.#hydrateEmptyState();
      return;
    }

    const queryTrigger = target.closest('[data-search-query]');
    if (queryTrigger instanceof HTMLElement) {
      event.preventDefault();
      const query = queryTrigger.dataset.searchQuery?.trim();
      if (!query) return;

      this.refs.searchInput.value = query;
      this.#showResetButton();
      this.#getSearchResults(query);
      this.refs.searchInput.focus();
      return;
    }

    if (target.closest('[data-view-all-results], [data-predictive-product-link]')) {
      this.#storeRecentSearch(this.refs.searchInput.value);
      return;
    }

    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement) {
      this.refs.searchInput.focus();
    }
  };

  search = debounce((event) => {
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 250);

  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.dialog?.closeDialog();
      return;
    }

    if (!this.#allResultsItems.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;
      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;
      case 'Tab':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        if (event.shiftKey) {
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;
      case 'Enter': {
        if (this.#currentIndex >= 0) {
          event.preventDefault();
          const interactiveChild = this.#currentItem?.querySelector('a, button');
          if (interactiveChild instanceof HTMLElement) interactiveChild.click();
        }
        break;
      }
    }
  };

  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) this.refs.searchInput.focus();
    this.#resetSearch();
  }, 100);

  async #getSearchResults(searchTerm) {
    const { predictiveSearchResults } = this.refs;
    const abortController = this.#createAbortController();

    this.setAttribute('loading', '');
    this.#setExpanded(true);
    predictiveSearchResults.innerHTML = this.#getLoadingMarkup();

    try {
      const response = await fetch(this.#getPredictiveSearchUrl(searchTerm), {
        signal: abortController.signal,
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`Predictive search request failed with status ${response.status}`);
      }

      const payload = await response.json();
      if (abortController.signal.aborted) return;

      const products = this.#normalizeProducts(payload);
      const queries = this.#normalizeQueries(payload);

      predictiveSearchResults.innerHTML = this.#renderPredictiveResults(searchTerm, products, queries);
      this.removeAttribute('loading');
      this.#resetScrollPositions();
    } catch (error) {
      if (abortController.signal.aborted) return;
      this.removeAttribute('loading');
      predictiveSearchResults.innerHTML = this.#getErrorMarkup();
      console.error(error);
    }
  }

  #getPredictiveSearchUrl(searchTerm) {
    const url = new URL(Theme.routes.predictive_search_url, location.origin);

    if (!url.pathname.endsWith('.json')) {
      url.pathname = `${url.pathname}.json`;
    }

    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'product,query');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor');
    url.searchParams.set('resources[options][unavailable_products]', 'hide');

    return url.toString();
  }

  #normalizeProducts(payload) {
    const results = payload?.resources?.results ?? payload?.resources ?? {};
    return Array.isArray(results.products) ? results.products : [];
  }

  #normalizeQueries(payload) {
    const results = payload?.resources?.results ?? payload?.resources ?? {};
    return Array.isArray(results.queries) ? results.queries : [];
  }

  #renderPredictiveResults(searchTerm, products, queries) {
    const productMarkup = products
      .slice(0, 6)
      .map((product, index) => this.#renderProductRow(product, index))
      .join('');

    const queryMarkup = queries.length
      ? `
        <div class="lp-predictive-search__queries">
          <h3 class="lp-predictive-search__featured-title">Trending searches</h3>
          <div class="lp-search-suggestions__list">
            ${queries
              .slice(0, 6)
              .map(
                (query) => `
                  <button
                    type="button"
                    class="lp-search-suggestions__chip"
                    data-search-query="${this.#escapeHtml(query.text || '')}"
                    data-predictive-item
                  >
                    ${this.#escapeHtml(query.text || '')}
                  </button>
                `
              )
              .join('')}
          </div>
        </div>
      `
      : '';

    if (!products.length) {
      return `
        <div class="lp-predictive-search__no-results" data-search-results>
          <div class="lp-predictive-search__empty-icon svg-wrapper">
            <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7">
              <circle cx="11" cy="11" r="6.5"></circle>
              <path d="m16 16 4.5 4.5" stroke-linecap="round"></path>
            </svg>
          </div>
          <h3 class="lp-predictive-search__empty-heading">No results found for "${this.#escapeHtml(searchTerm)}"</h3>
          <p class="lp-predictive-search__empty-copy">Try refining your search or browse featured pieces below.</p>
          ${queryMarkup}
        </div>
      `;
    }

    return `
      <div class="lp-predictive-search__inner" data-search-results>
        <div class="lp-predictive-search__meta">
          <p class="lp-predictive-search__count">
            ${products.length} ${products.length === 1 ? 'result' : 'results'} for "${this.#escapeHtml(searchTerm)}"
          </p>
        </div>
        <div class="lp-predictive-search__results-list">
          ${productMarkup}
        </div>
        ${queryMarkup}
      </div>
    `;
  }

  #renderProductRow(product, index) {
    const imageUrl =
      product?.featured_image?.url ||
      product?.featured_image ||
      product?.image ||
      '';

    const title = product?.title || '';
    const type = product?.product_type || product?.type || product?.vendor || '';
    const price = this.#formatProductPrice(product);
    const url = product?.url || '#';

    return `
      <div class="lp-predictive-search__result" data-predictive-item aria-selected="${index === 0 ? 'true' : 'false'}">
        <article class="lp-predictive-row">
          <a class="lp-predictive-row__link" href="${this.#escapeAttribute(url)}" data-predictive-product-link>
            <div class="lp-predictive-row__media">
              ${
                imageUrl
                  ? `<img class="lp-predictive-row__image" src="${this.#escapeAttribute(imageUrl)}" alt="${this.#escapeAttribute(title)}" loading="lazy">`
                  : ''
              }
            </div>
            <div class="lp-predictive-row__content">
              <h3 class="lp-predictive-row__title">${this.#escapeHtml(title)}</h3>
              ${type ? `<p class="lp-predictive-row__meta">${this.#escapeHtml(type)}</p>` : ''}
            </div>
            <div class="lp-predictive-row__price">${price}</div>
          </a>
        </article>
      </div>
    `;
  }

  #formatProductPrice(product) {
    const rawPrice = product?.price ?? product?.price_min ?? null;
    if (rawPrice == null || rawPrice === '') return '';

    if (typeof rawPrice === 'string' && /[$€£]/.test(rawPrice)) {
      return this.#escapeHtml(rawPrice);
    }

    const moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || 'USD';
    const minorUnits = convertMoneyToMinorUnits(String(rawPrice), currency);
    if (minorUnits === null) return this.#escapeHtml(String(rawPrice));

    return this.#escapeHtml(formatMoney(minorUnits, moneyFormat, currency));
  }

  #storeRecentSearch(query) {
    const normalizedQuery = query.trim();
    if (!normalizedQuery) return;

    const currentSearches = this.#getRecentSearches().filter(
      (item) => item.toLowerCase() !== normalizedQuery.toLowerCase()
    );

    currentSearches.unshift(normalizedQuery);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(currentSearches.slice(0, MAX_RECENT_SEARCHES)));
  }

  #getRecentSearches() {
    try {
      const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
      const parsed = JSON.parse(raw || '[]');
      return Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string') : [];
    } catch {
      return [];
    }
  }

  #clearRecentSearches() {
    localStorage.removeItem(RECENT_SEARCHES_KEY);
  }

  #hydrateEmptyState() {
    this.refs.predictiveSearchResults.innerHTML = this.#emptyStateMarkup;
    this.#renderRecentSearches();
    this.#hideResetButton();
    this.#setExpanded(false);
    this.removeAttribute('loading');
    this.#resetScrollPositions();
  }

  #renderRecentSearches() {
    const wrapper = this.refs.predictiveSearchResults.querySelector('[data-recent-searches-wrapper]');
    const list = this.refs.predictiveSearchResults.querySelector('[data-recent-searches-list]');
    if (!(wrapper instanceof HTMLElement) || !(list instanceof HTMLElement)) return;

    const recentSearches = this.#getRecentSearches();
    if (!recentSearches.length) {
      wrapper.hidden = true;
      list.innerHTML = '';
      return;
    }

    wrapper.hidden = false;
    list.innerHTML = recentSearches
      .map(
        (term, index) => `
          <button
            type="button"
            class="lp-search-suggestions__chip"
            data-search-query="${this.#escapeAttribute(term)}"
            data-predictive-item
            aria-selected="${index === 0 ? 'true' : 'false'}"
          >
            ${this.#escapeHtml(term)}
          </button>
        `
      )
      .join('');
  }

  #hideResetButton() {
    this.refs.resetButton.hidden = true;
  }

  #showResetButton() {
    this.refs.resetButton.hidden = false;
  }

  #setExpanded(isExpanded) {
    this.refs.searchInput.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) this.#activeFetch.abort();
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch() {
    this.#createAbortController();
    this.refs.searchInput.value = '';
    this.#currentIndex = -1;
    this.#hydrateEmptyState();
  }

  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  #getLoadingMarkup() {
    return `
      <div class="lp-search__status" data-search-results>
        <div>
          <div class="lp-search__status-spinner" aria-hidden="true"></div>
          <p class="lp-search__status-copy">Searching...</p>
        </div>
      </div>
    `;
  }

  #getErrorMarkup() {
    return `
      <div class="lp-search__status" data-search-results>
        <div>
          <p class="lp-search__status-copy">Search is temporarily unavailable. Please try again.</p>
        </div>
      </div>
    `;
  }

  #escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  #escapeAttribute(value) {
    return this.#escapeHtml(value);
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
